import { REGION } from '@env';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth/services';

import { FlockV3LprService } from '../../integrations';
import { ViLPRProvider } from '../../shared/models';
import { getBoundingBox, QueriesSearchParams } from '../../utils';
import { LPR_ERRORS, LprWarningEntry } from '../constants';
import {
  PLATE_STATE_DISTRICT_OF_COLUMBIA,
  PLATE_STATE_WASHINGTON_DC,
} from '../constants/parameters';
import {
  IFsHistoricalProviderResultsInterface,
  IFsLprFlockV3Parameters,
  IFsLprParameters,
  IFsLprProviderInterface,
  IFsLprResult,
  FlockV3LprResults,
  FlockV3Sort,
} from '../interfaces';
import { LprReadsFieldsModel } from '../models';
@Injectable({ providedIn: 'root' })
export class FlockV3ProviderService implements IFsLprProviderInterface, OnDestroy {
  private static readonly defaultRadiusInMiles: number = 25;
  private static readonly metersPerMile = 1609.344;
  private static readonly dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSSXXXXX";
  private static readonly maxPageSize = 50;

  public name = 'Flock';
  public nameEnum?: ViLPRProvider;

  public get loaded$(): Observable<boolean> {
    return this._loaded$;
  }

  public get historicLoaded$(): Observable<boolean> {
    return this._historicLoaded$;
  }

  get region(): string {
    return REGION;
  }

  // Error array to push any errors to be displayed.
  public errors: string[] = [];

  // New warnings array for non-critical filter limitations
  public warnings: LprWarningEntry[] = [];

  public isLoading: boolean;
  public isInit: boolean;
  public isFinished: boolean = false;
  public fields: LprReadsFieldsModel;

  private _params: IFsLprFlockV3Parameters;
  private _loaded$: Subject<boolean> = new Subject<boolean>();
  private _historicLoaded$: Subject<boolean> = new Subject<boolean>();
  private _inited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _flockNextPage: string | null = null;
  private readonly _destroy$: Subject<void> = new Subject<void>();
  private readonly _cancel$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _flockV3LprService: FlockV3LprService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _translocoService: TranslocoService,
  ) {
    if (this._sdmEnabled) {
      this.nameEnum = ViLPRProvider.FLOCK_V3;
      this.isLoading = true;
      this.isInit = false;
      this.total = 0;
    }
  }

  public results: IFsLprResult[] = [];
  public total: number = 0;

  /**
   * Initialize the provider
   */
  public init(): void {
    this.isInit = true;
    this._inited$.next(true);
  }

  public search(query: Partial<IFsLprParameters>): void {
    const inited$ = this._sdmEnabled
      ? this._inited$.pipe(
          filter((inited) => inited),
          take(1),
        )
      : this._inited$;
    inited$.subscribe((inited) => {
      if (inited) {
        this._startSearch(query);
      }
    });
  }

  /**
   * Used for pagination and to check if there're next pages.
   * @returns Does not return anything.
   */
  public next(): void {
    if (this.isFinished || this.isLoading) {
      return;
    }
    // If there are errors, we don't want to load the next page
    if (this.errors.length > 0) {
      this.isLoading = false;
      return;
    }
    if (this._flockNextPage !== null) {
      // Flock V3 API requires either the initial search params or just the pageId for pagination.
      // We send the full params during the first search and then send only the pageId
      // (since the search context is preserved by Flock V3 API) during pagination.
      this._params = { pageId: this._flockNextPage };
    } else if (this._flockNextPage === null) {
      this.isFinished = true;
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    this._flockV3LprService
      .getFlockPlatesV3(this._params)
      .pipe(
        takeUntil(this._cancel$),
        catchError((err) => {
          this.errors.push(this._parseApiError(err));
          this.isLoading = false;
          this.isFinished = true;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  public getHistoricalResults(
    searchParams: Partial<IFsLprParameters>,
  ): Observable<IFsHistoricalProviderResultsInterface> {
    const modifiedParams = this._historicalSearchTransformParams(searchParams);

    return this._flockV3LprService.getFlockPlatesV3(modifiedParams).pipe(
      map((response) => {
        return {
          results: response.results || [],
          total: response.results.length || 0,
        };
      }),
      catchError(() => {
        return of(null);
      }),
      takeUntil(this._destroy$),
    );
  }

  public cancel(): void {
    this._cancel$.next();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._cancel$.next();
    this._cancel$.complete();
  }

  // Added as a fallback helper function to get the point and radius from the bounding box
  public getPointAndRadius(
    params: Partial<IFsLprParameters>,
    bbox?: string,
  ): { point?: string; radius?: number } {
    if (params.point && params.radius) {
      return {
        point: params.point,
        radius: params.radius,
      };
    }

    // Calculate point and radius from bounding box if available
    if (bbox) {
      const coords = bbox.split(',').map(Number);
      if (coords.length === 4) {
        const [minLon, maxLat, maxLon, minLat] = coords;

        const centerLat = (maxLat + minLat) / 2;
        const centerLon = (maxLon + minLon) / 2;

        const latDiff = maxLat - minLat;
        const lonDiff = maxLon - minLon;
        const diagonalDistance = Math.sqrt(latDiff * latDiff + lonDiff * lonDiff);

        const radiusInMeters = diagonalDistance * 111000; // Rough conversion: 1 degree ≈ 111km

        return {
          point: `${centerLon},${centerLat}`,
          radius: Math.ceil(radiusInMeters),
        };
      }
    }

    return {};
  }

  private get _sdmEnabled(): boolean {
    return this._currentOrganization?.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  private _startSearch(query: Partial<IFsLprParameters>): void {
    this.cancel(); // Cancel any ongoing search
    this.errors = [];
    this.warnings = [];
    this.results = [];
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }

    const params = this._searchTransformParams(query);
    if (this.errors.length > 0) {
      this.isLoading = false;
      this.results = []; // reset results to avoid showing old results after errors
      this.total = 0;
      this._loaded$.next(true);
      return;
    }
    this._flockV3LprService
      .getFlockPlatesV3(params)
      .pipe(
        takeUntil(this._cancel$),
        catchError((err) => {
          this.errors.push(this._parseApiError(err));
          this.isLoading = false;
          this.total = 0;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  private _handleResponse(response: FlockV3LprResults): void {
    this._flockNextPage = null;
    if (!response) {
      this.isLoading = false;
      this._loaded$.next(true);
      this.isFinished = true;
      return;
    }
    if (response) {
      if (response.links.next) {
        const nextPageArray = response.links.next.split('=');
        if (nextPageArray.length === 2) {
          this._flockNextPage = nextPageArray[1];
        }
      }
      const items = response.results || [];
      this.total = response.resultCount || 0;
      this.results = [...this.results, ...items];
      this.isLoading = false;

      this.isFinished =
        this.results.length >= this.total || !this._flockNextPage || items.length === 0;

      this._loaded$.next(true);
    }
  }

  private _baseTransformParams(params: Partial<IFsLprParameters>): IFsLprFlockV3Parameters {
    const {
      fromDate: _fromDate,
      toDate: _toDate,
      plate: _plate,
      pageSize: _pageSize,
      sort,
    } = params;

    const [fromDate, toDate] = this._getFormattedDates(_fromDate?.toString(), _toDate?.toString());

    const pageSize =
      _pageSize && _pageSize <= FlockV3ProviderService.maxPageSize
        ? _pageSize
        : FlockV3ProviderService.maxPageSize;
    const reason = params.searchReason;
    const sortOrder = sort;
    const plateState = params.plateState;
    const caseNumber = params.caseNumber;

    return {
      plate: _plate,
      fromDate,
      toDate,
      pageSize,
      sortOrder,
      reason,
      plateState,
      caseNumber,
    };
  }

  private _searchTransformParams(params: Partial<IFsLprParameters>): IFsLprFlockV3Parameters {
    const copyOfParams = { ...params };

    // Flock V3 API requires a plate to be provided
    if (!copyOfParams.plate || copyOfParams.plate?.includes('*')) {
      this.errors.push(LPR_ERRORS.PLATE_REQUIRED);
    }

    // Map district_of_columbia to washington_dc for Flock V3 API compatibility
    if (copyOfParams.plateState === PLATE_STATE_DISTRICT_OF_COLUMBIA) {
      copyOfParams.plateState = PLATE_STATE_WASHINGTON_DC;
    }

    // Flock V3 API does not support vehicle color
    if (copyOfParams.vehicleColor?.length) {
      this.warnings.push({
        type: 'VEHICLE_COLOR_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_COLOR_NOT_SUPPORTED,
      });
      delete copyOfParams.vehicleColor;
    }

    // Flock V3 API does not support vehicle make
    if (copyOfParams.vehicleMake) {
      this.warnings.push({
        type: 'VEHICLE_MAKE_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_MAKE_NOT_SUPPORTED,
      });
      delete copyOfParams.vehicleMake;
    }

    // Flock V3 API does not support vehicle body
    if (copyOfParams.vehicleBody) {
      this.warnings.push({
        type: 'VEHICLE_BODY_NOT_SUPPORTED',
        message: LPR_ERRORS.VEHICLE_BODY_NOT_SUPPORTED,
      });
      delete copyOfParams.vehicleBody;
    }

    if (copyOfParams.assignedUserIds?.length) {
      this.warnings.push({
        type: 'ASSIGNED_USER_IDS_NOT_SUPPORTED',
        message: LPR_ERRORS.ASSIGNED_USER_IDS_NOT_SUPPORTED,
      });
      delete copyOfParams.assignedUserIds;
    }

    if (copyOfParams.deviceNames?.length) {
      this.warnings.push({
        type: 'DEVICE_NAMES_NOT_SUPPORTED',
        message: LPR_ERRORS.DEVICE_NAMES_NOT_SUPPORTED,
      });
      delete copyOfParams.deviceNames;
    }

    const inBbox = this._getBoundingBox(copyOfParams.inBbox);

    this._params = {
      ...this._baseTransformParams(copyOfParams),
      inBbox,
    };

    return this._params;
  }

  private _historicalSearchTransformParams(
    params: Partial<IFsLprParameters>,
  ): IFsLprFlockV3Parameters {
    return this._baseTransformParams(params);
  }

  private _getFormattedDates(fromDate, toDate): string[] {
    if (this.region !== 'US') {
      fromDate = fromDate
        ? format(new Date(fromDate), FlockV3ProviderService.dateFormat).replace('+', '-')
        : null;
      toDate = toDate
        ? format(new Date(toDate), FlockV3ProviderService.dateFormat).replace('+', '-')
        : null;
    } else {
      fromDate = fromDate ? format(new Date(fromDate), FlockV3ProviderService.dateFormat) : null;
      toDate = toDate ? format(new Date(toDate), FlockV3ProviderService.dateFormat) : null;
    }
    return [fromDate, toDate];
  }

  private _getBoundingBox(_inBbox: QueriesSearchParams): string | undefined {
    // Only include bbox if explicitly provided (drawn shape or "search this map" clicked)
    // Do not set default bbox based on organization center
    let inBbox;
    if (_inBbox) {
      inBbox = getBoundingBox(
        _inBbox,
        this._sdmEnabled && this.nameEnum ? this.nameEnum : this.name,
      );
    }

    return inBbox;
  }

  private _parseApiError(error: unknown): string {
    // Check if it's an HttpErrorResponse and extract the actual error from error.error
    let actualError = error;
    if (error && typeof error === 'object' && 'error' in error) {
      actualError = (error as any).error;
    }

    // Type guard to check if error has the expected structure
    const isApiError = (
      err: unknown,
    ): err is {
      code?: string;
      message?: string;
      flockMessage?: string;
      detail?: string;
    } => {
      return typeof err === 'object' && err !== null && 'message' in err;
    };

    if (isApiError(actualError)) {
      // Prioritize flockMessage if available (most user-friendly)
      if (actualError.flockMessage) {
        return this._translocoService.translate('vehicleIntelligence.errors.providerError', {
          message: actualError.flockMessage,
        });
      }

      // Fallback to detail if available
      if (actualError.detail) {
        return actualError.detail;
      }

      // Handle other structured error responses
      if (actualError.message) {
        return this._translocoService.translate('vehicleIntelligence.errors.providerError', {
          message: actualError.message,
        });
      }
    }

    // Fallback to generic error message
    return `${LPR_ERRORS.FAILED_TO_LOAD_SEARCH_RESULTS}`;
  }
}
